import React, { useState, useCallback, useRef } from 'react';
import { api } from '../api';
import { NS_GROUPS, NS_MULTILINE, NS_SELECT_OPTIONS, NS_MULTI_SELECT, nsPushValue, AU_SUBSIDIARY_ID, nsValuesFromSheet } from '../nsFields';
import { nsConnectMessage } from './NetSuiteConnect';
import DatasheetPreview from './DatasheetPreview';
import { exportPagesToPdfBytes } from '../pdfExport';

function MultiPicker({ label, value, options, multiValue, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const picked = String(value || '').split(',').map(s => s.trim()).filter(Boolean);
  function toggle(opt) {
    const next = picked.includes(opt) ? picked.filter(p => p !== opt) : [...picked, opt];
    onChange(next.join(', '));
  }
  return (
    <div className="nsp-multi">
      <button type="button" className="input nsp-multi-btn" data-testid={testid} onClick={() => setOpen(o => !o)}>
        <span>{picked.length ? picked.join(', ') : 'Select…'}</span>
        <span className="nsp-multi-caret">▾</span>
      </button>
      {open && (
        <div className="nsp-multi-panel">
          {options.map(opt => (
            <label key={opt} className="nsp-multi-opt">
              <input type="checkbox" checked={picked.includes(opt)} onChange={() => toggle(opt)} />
              {opt}
            </label>
          ))}
          {picked.length > 0 && !options.includes(picked[0]) && picked.length === 1 && null}
        </div>
      )}
      {picked.length > 1 && multiValue && (
        <div className="nsp-hint">NetSuite stores one value — this will be pushed as “{multiValue}”.</div>
      )}
    </div>
  );
}

function FieldInput({ label, value, onChange, testid }) {
  const multi = NS_MULTI_SELECT[label];
  if (multi) {
    return <MultiPicker label={label} value={value} options={multi.options} multiValue={multi.multiValue} onChange={onChange} testid={testid} />;
  }
  const opts = NS_SELECT_OPTIONS[label];
  if (opts) {
    const extra = value && !opts.includes(value) ? [value] : [];
    return (
      <select className="input" data-testid={testid} value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">- None -</option>
        {extra.map(o => <option key={o} value={o}>{o}</option>)}
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (NS_MULTILINE.includes(label)) {
    return <textarea className="input" rows={3} data-testid={testid} value={value || ''} onChange={e => onChange(e.target.value)} />;
  }
  return <input className="input" data-testid={testid} value={value || ''} onChange={e => onChange(e.target.value)} />;
}

function ItemSearch({ placeholder, onPick, testid }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  async function run() {
    if (!q.trim()) return;
    setBusy(true);
    try { const { items } = await api.nsSearch(q.trim()); setResults(items || []); }
    catch (e) { setResults([]); }
    setBusy(false);
  }
  return (
    <div className="nsp-search">
      <div className="nsp-search-row">
        <input className="input" data-testid={testid} placeholder={placeholder} value={q}
          onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') run(); }} />
        <button className="btn btn-sm" onClick={run} disabled={busy}>{busy ? '…' : 'Search'}</button>
      </div>
      {results !== null && (
        <div className="nsp-search-results">
          {results.length === 0 ? <div className="empty-note">No NetSuite items found.</div> : (
            <div className="nsp-search-count">{results.length} match{results.length === 1 ? '' : 'es'}{results.length >= 100 ? ' (first 100 shown — refine your search)' : ''}</div>
          )}
          {results.map(r => (
            <button key={r.id} className="nsp-search-hit" data-testid={'nsp-hit-' + r.id}
              onClick={() => { onPick(r); setResults(null); setQ(''); }}>
              <strong>{r.itemid}</strong>
              <span>{r.displayname}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function VendorPanel({ vendor, setVendor, toast }) {
  const [showCopy, setShowCopy] = useState(false);
  const [vq, setVq] = useState('');
  const [vres, setVres] = useState(null);
  const [vbusy, setVbusy] = useState(false);
  async function searchVendors() {
    if (!vq.trim()) return;
    setVbusy(true);
    try { setVres(await api.nsVendors(vq.trim())); }
    catch (e) { setVres({ available: false, items: [] }); }
    setVbusy(false);
  }
  async function copyFromItem(hit) {
    try {
      const info = await api.nsItemFields(hit.id);
      const v = info.vendor || {};
      if (!v.vendor_id) { toast('That item has no vendor line.', true); return; }
      setVendor(prev => ({
        ...prev,
        vendor_id: v.vendor_id,
        vendor_name: v.vendor_name || ('Vendor #' + v.vendor_id),
      }));
      setShowCopy(false);
      toast('Vendor copied: ' + (v.vendor_name || v.vendor_id));
    } catch (e) { toast('Could not read vendor: ' + (e.response?.data?.detail || e.message), true); }
  }
  return (
    <div className="nsp-vendor" data-testid="nsp-vendor-panel">
      <div className="nsp-group-title">Vendor &amp; purchasing (manual)</div>
      <div className="nsp-vendor-grid">
        <label>Vendor
          <div className="nsp-vendor-pick">
            <input className="input" data-testid="nsp-vendor-id" placeholder="Vendor internal ID"
              value={vendor.vendor_id || ''}
              onChange={e => setVendor(v => ({ ...v, vendor_id: e.target.value, vendor_name: '' }))} />
            <button className="btn btn-sm" data-testid="nsp-vendor-copy-btn" onClick={() => setShowCopy(s => !s)}
              title="Search an existing NetSuite item and copy its preferred vendor">Copy from item</button>
          </div>
          {vendor.vendor_name && <div className="nsp-vendor-name" data-testid="nsp-vendor-name">{vendor.vendor_name}</div>}
          {showCopy && <ItemSearch placeholder="Search item to copy vendor from…" testid="nsp-vendor-copy-search" onPick={copyFromItem} />}
          <div className="nsp-vendor-search">
            <div className="nsp-search-row">
              <input className="input" data-testid="nsp-vendor-search" placeholder="Search vendors by name…" value={vq}
                onChange={e => setVq(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchVendors(); } }} />
              <button type="button" className="btn btn-sm" onClick={searchVendors} disabled={vbusy}>{vbusy ? '…' : 'Search'}</button>
            </div>
            {vres && !vres.available && (
              <div className="nsp-hint" data-testid="nsp-vendor-unavailable">
                Vendor search needs the "Lists → Vendors (View)" permission on the integration role — use "Copy from item" or enter the internal ID for now.
              </div>
            )}
            {vres && vres.available && (
              <div className="nsp-search-results">
                {vres.items.length === 0 && <div className="empty-note">No vendors found.</div>}
                {vres.items.map(v => (
                  <button type="button" key={v.id} className="nsp-search-hit" data-testid={'nsp-vendor-hit-' + v.id}
                    onClick={() => { setVendor(p => ({ ...p, vendor_id: String(v.id), vendor_name: v.companyname || v.entityid })); setVres(null); setVq(''); }}>
                    <strong>{v.companyname || v.entityid}</strong>
                    <span>#{v.id}{v.entityid && v.companyname ? ' · ' + v.entityid : ''}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </label>
        <label>Vendor code
          <input className="input" data-testid="nsp-vendor-code" value={vendor.vendor_code || ''}
            onChange={e => setVendor(v => ({ ...v, vendor_code: e.target.value }))} placeholder="e.g. YL30-48W-1200 G2" />
        </label>
        <label>Purchase price (USD)
          <input className="input" data-testid="nsp-purchase-price" type="number" step="0.01" value={vendor.purchase_price ?? ''}
            onChange={e => setVendor(v => ({ ...v, purchase_price: e.target.value }))} placeholder="e.g. 17.89" />
        </label>
        <label>Subsidiary ID
          <input className="input" data-testid="nsp-subsidiary" value={vendor.subsidiary_id || ''}
            onChange={e => setVendor(v => ({ ...v, subsidiary_id: e.target.value }))} />
          <span className="nsp-hint">{vendor.subsidiary_id === AU_SUBSIDIARY_ID ? 'Australia' : ''}</span>
        </label>
      </div>
    </div>
  );
}

export function PushCard({ entry, toast, onStatus }) {
  const [mode, setMode] = useState('create');
  const [values, setValues] = useState(() => nsValuesFromSheet(entry.sheet, entry.productId));
  const [current, setCurrent] = useState(null); // existing item {id, fields, vendor, url}
  const [vendor, setVendor] = useState({ subsidiary_id: AU_SUBSIDIARY_ID });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(true);
  const [spBusy, setSpBusy] = useState(false);
  const exportRef = useRef(null);

  async function pdfToSharePoint() {
    setSpBusy(true);
    try {
      const bytes = await exportPagesToPdfBytes(exportRef.current, { title: entry.sheet.name || 'Datasheet' });
      if (!bytes) throw new Error('Could not render the datasheet PDF');
      const fname = (entry.sheet.code || entry.sheet.name || 'datasheet').replace(/[^a-zA-Z0-9_-]+/g, '_') + '.pdf';
      const res = await api.spUploadDatasheet(bytes, fname);
      setValues(v => ({ ...v, 'DATASHEET LINK': res.url || '' }));
      toast('Uploaded to SharePoint — Datasheet Link filled');
    } catch (e) {
      const st = e.response?.status;
      const msg = e.response?.data?.detail || e.message;
      toast(st === 503 ? 'SharePoint is not connected yet — the Client Secret is still missing.' : 'SharePoint upload failed: ' + msg, true);
    }
    setSpBusy(false);
  }

  const pickExisting = useCallback(async (hit) => {
    setBusy(true); setError(null);
    try {
      const info = await api.nsItemFields(hit.id);
      setCurrent(info);
      setValues(v => ({ ...v, 'ITEM NAME/NUMBER INTERNAL': info.fields['ITEM NAME/NUMBER INTERNAL'] || v['ITEM NAME/NUMBER INTERNAL'] }));
      if (info.vendor && info.vendor.vendor_id) {
        setVendor(prev => ({
          ...prev,
          vendor_id: info.vendor.vendor_id,
          vendor_name: info.vendor.vendor_name,
          vendor_code: info.vendor.vendor_code || prev.vendor_code,
          purchase_price: info.vendor.purchase_price ?? prev.purchase_price,
          subsidiary_id: info.vendor.subsidiary_id || prev.subsidiary_id,
        }));
      }
    } catch (e) { toast('Could not load item: ' + (e.response?.data?.detail || e.message), true); }
    setBusy(false);
  }, [toast]);

  async function push() {
    if (mode === 'update' && !current) { toast('Pick the existing item to merge into first.', true); return; }
    if (mode === 'create' && !String(values['ITEM NAME/NUMBER INTERNAL'] || '').trim()) {
      toast('ITEM NAME/NUMBER INTERNAL is required to create an item.', true); return;
    }
    setBusy(true); setError(null);
    try {
      const pushFields = {};
      Object.keys(values).forEach(k => { pushFields[k] = nsPushValue(k, values[k]); });
      const body = {
        mode,
        item_id: mode === 'update' ? current.id : null,
        product_id: entry.productId || null,
        fields: pushFields,
        vendor: {
          vendor_id: vendor.vendor_id || null,
          vendor_code: vendor.vendor_code || null,
          purchase_price: vendor.purchase_price === '' || vendor.purchase_price == null ? null : Number(vendor.purchase_price),
          subsidiary_id: vendor.subsidiary_id || null,
        },
      };
      const res = await api.nsPush(body);
      setResult(res);
      if (onStatus) onStatus(entry.key, { pushed: true, mode });
      toast(mode === 'create' ? 'Created item #' + res.id + ' in NetSuite' : 'Merged into item #' + res.id);
    } catch (e) {
      const msg = nsConnectMessage(e) || e.response?.data?.detail || e.message;
      setError(msg);
      toast('Push failed — see details on the card', true);
    }
    setBusy(false);
  }

  const empty = (v) => v == null || String(v).trim() === '';

  return (
    <div className="nsp-card" data-testid={'nsp-card-' + (entry.key || 'single')}>
      <div className="nsp-card-head" onClick={() => setOpen(o => !o)}>
        <span className={'caret' + (open ? ' open' : '')}>▸</span>
        <strong>{entry.name || entry.sheet.name || '(Untitled datasheet)'}</strong>
        {entry.sheet.code && <span className="nsp-code">{entry.sheet.code}</span>}
        {result && <span className="nsp-done" data-testid="nsp-done">✓ pushed #{result.id}</span>}
      </div>
      {open && (
        <div className="nsp-card-body">
          <div className="nsp-mode-row">
            <label className={'nsp-mode' + (mode === 'create' ? ' on' : '')}>
              <input type="radio" data-testid="nsp-mode-create" checked={mode === 'create'} onChange={() => setMode('create')} />
              Create as new item
            </label>
            <label className={'nsp-mode' + (mode === 'update' ? ' on' : '')}>
              <input type="radio" data-testid="nsp-mode-update" checked={mode === 'update'} onChange={() => setMode('update')} />
              Merge / override an existing item
            </label>
          </div>
          {mode === 'update' && (
            <div className="nsp-target">
              {current ? (
                <div className="nsp-target-picked" data-testid="nsp-target-picked">
                  Merging into: <strong>{current.fields['ITEM NAME/NUMBER INTERNAL']}</strong>
                  <a href={current.url} target="_blank" rel="noreferrer">open in NetSuite ↗</a>
                  <button className="btn btn-sm" onClick={() => setCurrent(null)}>Change</button>
                </div>
              ) : (
                <ItemSearch placeholder="Search NetSuite item to replace (name / code)…" testid="nsp-target-search" onPick={pickExisting} />
              )}
              <div className="nsp-hint">Only fields with a value below are pushed — blank fields leave the NetSuite value untouched.</div>
            </div>
          )}

          <VendorPanel vendor={vendor} setVendor={setVendor} toast={toast} />

          {NS_GROUPS.map(g => (
            <div className="nsp-group" key={g.title}>
              <div className="nsp-group-title">{g.title}</div>
              <div className="nsp-grid">
                {g.labels.map(label => (
                  <div className={'nsp-row' + (empty(values[label]) ? ' is-empty' : '')} key={label}>
                    <div className="nsp-label">{label}</div>
                    <div className="nsp-val">
                      <FieldInput label={label} value={values[label]} testid={'nsp-f-' + label}
                        onChange={val => setValues(v => ({ ...v, [label]: val }))} />
                      {label === 'DATASHEET LINK' && (
                        <button type="button" className="btn btn-sm nsp-sp-btn" data-testid="nsp-sharepoint-btn" disabled={spBusy}
                          onClick={pdfToSharePoint}
                          title="Render this datasheet as a PDF, upload it to the SharePoint datasheets site and fill this field with the share link">
                          {spBusy ? 'Uploading…' : 'PDF → SharePoint link'}
                        </button>
                      )}
                      {mode === 'update' && current && (
                        <div className="nsp-current" title="Current value in NetSuite">
                          NS: {current.fields[label] ? current.fields[label] : <em>— blank —</em>}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {error && <div className="nsp-error" data-testid="nsp-error">{error}</div>}
          <div className="nsp-actions">
            {result && <a className="btn btn-sm" href={result.url} target="_blank" rel="noreferrer">Open item in NetSuite ↗</a>}
            <button className="btn btn-primary" data-testid="nsp-push-btn" disabled={busy} onClick={push}>
              {busy ? 'Pushing…' : (mode === 'create' ? 'Create in NetSuite' : 'Merge into NetSuite item')}
            </button>
          </div>
          <div className="export-host" ref={exportRef} aria-hidden="true">
            <DatasheetPreview sheet={entry.sheet} pageId={'nsp-export-' + (entry.key || 'x')} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function NetSuitePush({ entries, onClose, toast }) {
  const [statuses, setStatuses] = useState({});
  const [active, setActive] = useState(entries[0] && entries[0].key);
  const scrollRef = useRef(null);

  const onStatus = useCallback((key, st) => setStatuses(s => ({ ...s, [key]: st })), []);

  function jumpTo(key) {
    setActive(key);
    const el = document.getElementById('nsp-anchor-' + key);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function onScroll() {
    const sc = scrollRef.current;
    if (!sc) return;
    const top = sc.getBoundingClientRect().top + 90;
    let cur = entries[0] && entries[0].key;
    for (const e of entries) {
      const el = document.getElementById('nsp-anchor-' + e.key);
      if (el && el.getBoundingClientRect().top <= top) cur = e.key;
    }
    setActive(cur);
  }

  return (
    <div className="nsp-overlay" data-testid="nsp-overlay">
      <div className="nsp-topbar">
        <div>
          <strong>Push to NetSuite</strong>
          <span className="nsp-sub">Review everything below — only fields with values are pushed. Sandbox account.</span>
        </div>
        <button className="btn" data-testid="nsp-close" onClick={onClose}>✕ Close</button>
      </div>
      <div className="nsp-body">
        <aside className="nsp-side" data-testid="nsp-sidebar">
          <div className="nsp-side-title">Items ({entries.length})</div>
          <div className="nsp-side-list">
            {entries.map(e => (
              <button key={e.key} className={'nsp-side-item' + (active === e.key ? ' active' : '')}
                data-testid={'nsp-side-' + e.key} onClick={() => jumpTo(e.key)}>
                <span className="nm">{e.name || e.sheet.name || '(Untitled)'}</span>
                {e.sheet.code && <span className="cd">{e.sheet.code}</span>}
                {statuses[e.key] && statuses[e.key].pushed && <span className="ok">✓ pushed</span>}
              </button>
            ))}
          </div>
        </aside>
        <div className="nsp-scroll" ref={scrollRef} onScroll={onScroll}>
          {entries.map(e => (
            <div id={'nsp-anchor-' + e.key} key={e.key} className="nsp-anchor">
              <PushCard entry={e} toast={toast} onStatus={onStatus} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
